<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import type { CategoryData, PageState } from '../types'
import {
  fetchToolsData,
  getCachedData,
  saveToCache,
  hasDataChanged,
  getCacheTimestamp
} from '../api/tools'
import LoadingState from '../components/LoadingState.vue'
import ErrorState from '../components/ErrorState.vue'
import EmptyState from '../components/EmptyState.vue'
import CategorySection from '../components/CategorySection.vue'
import StatsPanel from '../components/StatsPanel.vue'

const state = ref<PageState>('loading')
const error = ref<string>('')
const categories = ref<CategoryData[]>([])
const isLoading = ref(false)
const dataUpdated = ref(false)
const showUpdateToast = ref(false)
const updateToastMessage = ref('')
const activeCategory = ref<string>('')
const showMobileMenu = ref(false)
let observer: IntersectionObserver | null = null

// 从环境变量读取配置
const appTitle = import.meta.env.VITE_APP_TITLE || '魔法导航'

const totalLinks = computed(() => {
  return categories.value.reduce((sum, cat) => sum + cat.children.length, 0)
})

const totalCategories = computed(() => {
  return categories.value.length
})

const cacheTimestamp = computed(() => {
  const timestamp = getCacheTimestamp()
  if (!timestamp) return null
  const date = new Date(timestamp)
  return date.toLocaleString('zh-CN')
})

async function loadTools() {
  state.value = 'loading'
  error.value = ''
  isLoading.value = true
  dataUpdated.value = false

  try {
    // 1. 先从 LocalStorage 读取缓存数据并显示
    const cachedData = getCachedData()
    if (cachedData && cachedData.length > 0) {
      categories.value = cachedData
      state.value = 'content'
      // 设置默认选中第一个分类
      if (cachedData.length > 0 && !activeCategory.value) {
        activeCategory.value = cachedData[0].category
      }
    }

    // 2. 发送请求获取最新数据
    const newData = await fetchToolsData()

    if (newData.length === 0) {
      if (!cachedData || cachedData.length === 0) {
        state.value = 'empty'
      }
    } else {
      // 3. 检查数据是否有变化
      if (cachedData && cachedData.length > 0) {
        if (hasDataChanged(cachedData, newData)) {
          // 数据有变化，更新页面
          categories.value = newData
          saveToCache(newData)
          dataUpdated.value = true
          showUpdateToastMessage('数据已更新')
        } else {
          // 数据无变化，保持缓存数据
          showUpdateToastMessage('数据已是最新')
        }
      } else {
        // 没有缓存，直接使用新数据
        categories.value = newData
        saveToCache(newData)
        state.value = 'content'
        // 设置默认选中第一个分类
        if (newData.length > 0) {
          activeCategory.value = newData[0].category
        }
      }
    }
  } catch (err) {
    // 请求失败，如果有缓存则使用缓存数据
    const cachedData = getCachedData()
    if (cachedData && cachedData.length > 0) {
      categories.value = cachedData
      state.value = 'content'
      showUpdateToastMessage('使用缓存数据，网络请求失败')
    } else {
      state.value = 'error'
      error.value = err instanceof Error ? err.message : '加载失败，请稍后重试'
    }
  } finally {
    isLoading.value = false
  }
}

function selectCategory(category: string) {
  activeCategory.value = category
  showMobileMenu.value = false
  // 使用 hash 导航，浏览器会自动处理滚动
  const targetId = `category-${category}`
  const element = document.getElementById(targetId)
  if (element) {
    // 手动平滑滚动，设置 scroll-margin-top 避免被固定导航栏遮挡
    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

// 设置 IntersectionObserver 来监听滚动位置
function setupScrollObserver() {
  if (observer) {
    observer.disconnect()
  }

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // 找到当前进入视口的分类并高亮
          const categoryId = entry.target.id.replace('category-', '')
          activeCategory.value = categoryId
        }
      })
    },
    {
      rootMargin: '-20% 0px -60% 0px', // 在视口中间触发
      threshold: 0
    }
  )

  // 监听所有分类元素
  categories.value.forEach((category) => {
    const element = document.getElementById(`category-${category.category}`)
    if (element) {
      observer?.observe(element)
    }
  })
}

function showUpdateToastMessage(message: string) {
  updateToastMessage.value = message
  showUpdateToast.value = true
  setTimeout(() => {
    showUpdateToast.value = false
  }, 3000)
}

onMounted(async () => {
  await loadTools()
  // 数据加载完成后设置滚动监听
  setupScrollObserver()
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
    observer = null
  }
})
</script>

<template>
  <div class="min-h-screen bg-background">
    <!-- Fixed Navigation Bar -->
    <header class="fixed top-0 left-0 right-0 z-50 bg-card/95 bg-background border-b border-border shadow-sm">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-12">
          <!-- Logo/Title -->
          <h1 class="text-lg font-bold text-primary whitespace-nowrap mr-4 hidden md:block">
            {{ appTitle }}
          </h1>

          <!-- Desktop: Navigation Buttons -->
          <nav class="hidden md:flex items-center gap-1 overflow-x-auto no-scrollbar">
            <button
              v-for="category in categories"
              :key="category.category"
              @click="selectCategory(category.category)"
              :class="[
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap',
                activeCategory === category.category
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              ]"
            >
              {{ category.category }}
            </button>
          </nav>

          <!-- Mobile: Menu Button -->
          <button
            @click="showMobileMenu = true"
            class="md:hidden flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md bg-accent text-accent-foreground hover:bg-accent/80 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
            <span>{{ activeCategory || '选择分类' }}</span>
          </button>
        </div>
      </div>
    </header>

    <!-- Spacer for fixed header -->
    <div class="h-12"></div>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="bg-card rounded-xl shadow-sm p-6 md:p-9">
        <!-- Loading Toast -->
        <Transition
          enter-active-class="transition ease-out duration-300"
          enter-from-class="opacity-0 translate-y-[-10px]"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition ease-in duration-200"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-[-10px]"
        >
          <div
            v-if="isLoading"
            class="mb-6 p-4 bg-accent border border-border rounded-lg flex items-center gap-3"
          >
            <svg
              class="animate-spin h-5 w-5 text-primary"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span class="text-accent-foreground font-medium">正在获取最新数据...</span>
          </div>
        </Transition>

        <!-- Update Toast -->
        <Transition
          enter-active-class="transition ease-out duration-300"
          enter-from-class="opacity-0 translate-y-[-10px]"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition ease-in duration-200"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-[-10px]"
        >
          <div
            v-if="showUpdateToast"
            class="mb-6 p-4 bg-accent border border-border rounded-lg flex items-center gap-3"
          >
            <svg
              class="h-5 w-5 text-primary"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span class="text-accent-foreground font-medium">{{ updateToastMessage }}</span>
          </div>
        </Transition>

        <!-- Cache Info -->
        <Transition
          enter-active-class="transition ease-out duration-300"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition ease-in duration-200"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            v-if="cacheTimestamp && state === 'content' && !isLoading"
            class="mb-4 p-3 bg-muted border border-border rounded-lg text-sm text-muted-foreground"
          >
            <span class="font-medium">缓存时间：</span>{{ cacheTimestamp }}
          </div>
        </Transition>

        <!-- Loading State -->
        <LoadingState v-if="state === 'loading'" />

        <!-- Error State -->
        <ErrorState
          v-else-if="state === 'error'"
          :error="error"
          @retry="loadTools"
        />

        <!-- Empty State -->
        <EmptyState v-else-if="state === 'empty'" />

        <!-- Content State -->
        <div v-else>
          <CategorySection
            v-for="(category, index) in categories"
            :key="category.category"
            :category="category"
            :index="index"
            :active-category="activeCategory"
          />
          <StatsPanel
            :total-links="totalLinks"
            :total-categories="totalCategories"
          />
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="fixed bottom-0 left-1/2 -translate-x-1/2 py-3 px-6 bg-card/90 backdrop-blur-sm border border-border rounded-t-lg shadow-lg z-50">
      <div class="text-center text-muted-foreground text-sm">
        <p>© {{ new Date().getFullYear() }} 魔法代码</p>
      </div>
    </footer>

    <!-- Footer Spacer -->
    <div class="h-12"></div>

    <!-- Mobile Menu Modal -->
    <Transition
      enter-active-class="transition ease-out duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showMobileMenu"
        @click="showMobileMenu = false"
        class="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
      />
    </Transition>

    <Transition
      enter-active-class="transition ease-out duration-300"
      enter-from-class="opacity-0 translate-y-[-20px]"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-200"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-[-20px]"
    >
      <div
        v-if="showMobileMenu"
        class="fixed top-12 left-0 right-0 z-[60] bg-background border-b border-border shadow-lg p-4 md:hidden"
      >
        <div class="grid grid-cols-2 gap-2">
          <button
            v-for="category in categories"
            :key="category.category"
            @click="selectCategory(category.category)"
            :class="[
              'px-4 py-3 text-sm font-medium rounded-lg transition-colors text-left',
              activeCategory === category.category
                ? 'bg-primary text-primary-foreground'
                : 'bg-accent text-accent-foreground hover:bg-accent/80'
            ]"
          >
            {{ category.category }}
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>
